import ApiClient from "../generated/ApiClient";

// TODO: These two endpoints should be absorbed into the generated OpenAPI
// client (src/generated) at the next regeneration. This hand-written wrapper
// calls them through the same ApiClient (base path + AccessToken header) the
// generated *Api classes use, so swapping to a generated ChangeTrackingApi
// later is a drop-in change.

// The backend may serialize enums as strings ("complete") or as numbers,
// depending on serializer settings. Normalize both into canonical camelCase
// strings so the rest of the frontend can compare directly.
const STATUS_BY_NUMBER = {
  1: "queued",
  2: "processing",
  3: "complete",
  4: "failed",
  5: "skipped",
};
const CHANGE_TYPE_BY_NUMBER = {
  1: "modified",
  2: "added",
  3: "removed",
  5: "colorChange",
  6: "positionShift",
};
const SEVERITY_BY_NUMBER = {
  1: "minor",
  2: "normal",
};
const SKIP_REASON_BY_NUMBER = {
  1: "unchanged",
  2: "ambiguous",
  3: "unsupportedType",
  4: "missingRasters",
  5: "tooManyPages",
};

const STATUS_VALUES = ["queued", "processing", "complete", "failed", "skipped"];
const CHANGE_TYPE_VALUES = [
  "modified",
  "added",
  "removed",
  "colorChange",
  "positionShift",
  "unknown",
];
const SEVERITY_VALUES = ["minor", "normal"];
const SKIP_REASON_VALUES = [
  "unchanged",
  "ambiguous",
  "unsupportedType",
  "missingRasters",
  "tooManyPages",
];

function normalizeEnumValue(value, byNumber, canonicalValues, fallback) {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }
  if (typeof value === "number") {
    return byNumber[value] || fallback;
  }
  const str = String(value);
  if (/^\d+$/.test(str)) {
    return byNumber[parseInt(str, 10)] || fallback;
  }
  const lower = str.toLowerCase();
  for (let i = 0; i < canonicalValues.length; i++) {
    if (canonicalValues[i].toLowerCase() === lower) {
      return canonicalValues[i];
    }
  }
  return fallback;
}

function normalizeStatus(value) {
  return normalizeEnumValue(value, STATUS_BY_NUMBER, STATUS_VALUES, "queued");
}

function normalizeChangeType(value) {
  return normalizeEnumValue(
    value,
    CHANGE_TYPE_BY_NUMBER,
    CHANGE_TYPE_VALUES,
    "unknown"
  );
}

function normalizeSeverity(value) {
  return normalizeEnumValue(value, SEVERITY_BY_NUMBER, SEVERITY_VALUES, "normal");
}

function normalizeSkipReason(value) {
  return normalizeEnumValue(value, SKIP_REASON_BY_NUMBER, SKIP_REASON_VALUES, null);
}

function normalizeChange(change) {
  return Object.assign({}, change, {
    changeType: normalizeChangeType(change.changeType),
    severity: normalizeSeverity(change.severity),
    // per-user acknowledgement; tolerate servers that don't send it yet
    seen: !!change.seen,
  });
}

function normalizePage(page) {
  const changes = Array.isArray(page.changes) ? page.changes : [];
  return Object.assign({}, page, {
    // pageChangeType is only present for added/removed pages
    pageChangeType:
      page.pageChangeType === null || page.pageChangeType === undefined
        ? null
        : normalizeChangeType(page.pageChangeType),
    changes: changes.map(normalizeChange),
  });
}

function normalizeJob(job) {
  const pages = Array.isArray(job.pages) ? job.pages : [];
  return Object.assign({}, job, {
    status: normalizeStatus(job.status),
    skipReason: normalizeSkipReason(job.skipReason),
    normalChangeCount: job.normalChangeCount || 0,
    minorChangeCount: job.minorChangeCount || 0,
    seenNormalCount: job.seenNormalCount || 0,
    pages: pages.map(normalizePage),
  });
}

function normalizeChangeTrackingResponse(response) {
  if (!response || typeof response !== "object") {
    return null;
  }
  const jobs = Array.isArray(response.jobs) ? response.jobs : [];
  return Object.assign({}, response, {
    status: normalizeStatus(response.status),
    jobs: jobs.map(normalizeJob),
  });
}

class ChangeTrackingService {
  constructor() {
    this.apiClient = ApiClient.instance;
  }

  // Owner/admin endpoint (AccessToken = logged-in user's api token)
  async getForProof(proofId, fromVersionId, toVersionId) {
    const body = await window.$A.HandleApiResponse(
      this.apiClient.callApi(
        "/proof/{proofId}/change-tracking",
        "GET",
        { proofId: proofId },
        { fromVersionId: fromVersionId, toVersionId: toVersionId },
        {},
        {},
        null,
        ["AccessToken"],
        [],
        ["text/plain", "application/json", "text/json"],
        Object,
        null,
        null
      ),
      "",
      true
    );
    return normalizeChangeTrackingResponse(body);
  }

  // Reviewer endpoint (identity-based; AccessToken = approver token, same as
  // the existing /review/proof/{proofVersionId?} calls)
  async getForReview(fromVersionId, toVersionId) {
    const body = await window.$A.HandleApiResponse(
      this.apiClient.callApi(
        "/review/proof/change-tracking",
        "GET",
        {},
        { fromVersionId: fromVersionId, toVersionId: toVersionId },
        {},
        {},
        null,
        ["AccessToken"],
        [],
        ["text/plain", "application/json", "text/json"],
        Object,
        null,
        null
      ),
      "",
      true
    );
    return normalizeChangeTrackingResponse(body);
  }

  // Mirrors how proofView.v2.vue chooses between ProofService.GetFullProof
  // (curUser set) and ReviewService.GetProofToReview (curApprover set).
  async fetch({ curUser, curApprover, proofId, fromVersionId, toVersionId }) {
    if (curUser) {
      return this.getForProof(proofId, fromVersionId, toVersionId);
    }
    if (curApprover) {
      return this.getForReview(fromVersionId, toVersionId);
    }
    return null;
  }

  // Owner/admin endpoint — marks a change as seen/unseen for the current user
  async setSeenForProof(proofId, changeId, seen) {
    return await window.$A.HandleApiResponse(
      this.apiClient.callApi(
        "/proof/{proofId}/change-tracking/changes/{changeId}/seen",
        "POST",
        { proofId: proofId, changeId: changeId },
        {},
        {},
        {},
        { seen: seen },
        ["AccessToken"],
        ["application/json"],
        ["text/plain", "application/json", "text/json"],
        Object,
        null,
        null
      ),
      "",
      true
    );
  }

  // Reviewer endpoint (identity-based approver token)
  async setSeenForReview(changeId, seen) {
    return await window.$A.HandleApiResponse(
      this.apiClient.callApi(
        "/review/proof/change-tracking/changes/{changeId}/seen",
        "POST",
        { changeId: changeId },
        {},
        {},
        {},
        { seen: seen },
        ["AccessToken"],
        ["application/json"],
        ["text/plain", "application/json", "text/json"],
        Object,
        null,
        null
      ),
      "",
      true
    );
  }

  // Same owner/reviewer context selection as fetch(); returns {changeId, seen}
  // on success, null on failure
  async setSeen({ curUser, curApprover, proofId, changeId, seen }) {
    if (curUser) {
      return this.setSeenForProof(proofId, changeId, seen);
    }
    if (curApprover) {
      return this.setSeenForReview(changeId, seen);
    }
    return null;
  }
}

export {
  normalizeChangeTrackingResponse,
  normalizeStatus,
  normalizeChangeType,
  normalizeSeverity,
  normalizeSkipReason,
};
export default ChangeTrackingService;
